/**
 * Test Suites Hooks
 * React Query hooks for test suite management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { testSuitesApi } from '@/lib/api/evalScenarios';
import type {
  EvalTestSuite,
  CreateTestSuiteRequest,
  UpdateTestSuiteRequest,
  TestSuiteRunResult,
  EvalScenario,
} from '@/types/evalScenarios';

// Query keys
const QUERY_KEYS = {
  testSuites: ['testSuites'] as const,
  testSuite: (id: string) => ['testSuites', id] as const,
  suiteScenarios: (id: string) => ['testSuites', id, 'scenarios'] as const,
  suiteTags: ['testSuites', 'tags'] as const,
};

/**
 * Fetch all test suites
 */
export function useTestSuites(params?: { is_active?: boolean }) {
  return useQuery<EvalTestSuite[]>({
    queryKey: [...QUERY_KEYS.testSuites, params],
    queryFn: () => testSuitesApi.list(params),
  });
}

/**
 * Fetch a single test suite
 */
export function useTestSuite(suiteId: string | null) {
  return useQuery<EvalTestSuite>({
    queryKey: QUERY_KEYS.testSuite(suiteId || ''),
    queryFn: () => testSuitesApi.get(suiteId!),
    enabled: !!suiteId,
  });
}

/**
 * Fetch scenarios in a test suite
 */
export function useSuiteScenarios(suiteId: string | null) {
  return useQuery<EvalScenario[]>({
    queryKey: QUERY_KEYS.suiteScenarios(suiteId || ''),
    queryFn: () => testSuitesApi.getScenarios(suiteId!),
    enabled: !!suiteId,
  });
}

/**
 * Fetch all unique tags from test suites
 */
export function useSuiteTags() {
  return useQuery<string[]>({
    queryKey: QUERY_KEYS.suiteTags,
    queryFn: () => testSuitesApi.getTags(),
  });
}

/**
 * Create a new test suite
 */
export function useCreateTestSuite() {
  const queryClient = useQueryClient();

  return useMutation<EvalTestSuite, Error, CreateTestSuiteRequest>({
    mutationFn: (data) => testSuitesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.testSuites });
    },
  });
}

/**
 * Update a test suite
 */
export function useUpdateTestSuite() {
  const queryClient = useQueryClient();

  return useMutation<
    EvalTestSuite,
    Error,
    { suiteId: string; data: UpdateTestSuiteRequest }
  >({
    mutationFn: ({ suiteId, data }) => testSuitesApi.update(suiteId, data),
    onSuccess: (_, { suiteId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.testSuites });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.testSuite(suiteId) });
    },
  });
}

/**
 * Delete a test suite
 */
export function useDeleteTestSuite() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { suiteId: string; hardDelete?: boolean }>({
    mutationFn: ({ suiteId, hardDelete }) => testSuitesApi.delete(suiteId, hardDelete),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.testSuites });
    },
  });
}

/**
 * Run a test suite
 */
export function useRunTestSuite() {
  const queryClient = useQueryClient();

  return useMutation<TestSuiteRunResult, Error, { suiteId: string; modelId?: string }>({
    mutationFn: ({ suiteId, modelId }) => testSuitesApi.run(suiteId, modelId),
    onSuccess: () => {
      // Invalidate eval runs to show the new run
      queryClient.invalidateQueries({ queryKey: ['evalRuns'] });
    },
  });
}
